import Message from 'Components/Message';
import Modal from 'Components/Modal/Modal';
import { FormItem, Label } from 'Constants/styles';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';

import { FormCheckbox, FormWrapper, VInput } from './styles';
import { UpdateCheckoutLimitsFormProps } from './types';

type Props = {
  updateCheckoutLimitsFormProps: UpdateCheckoutLimitsFormProps | null;
  updateCheckoutLimitsHandler: (formData: UpdateCheckoutLimitsFormProps) => void;
  modalErrorMsg: string;
  isModalLoading: boolean;
  setShowModal: (showModal: boolean) => void;
};

const UpdateCheckoutLimitsModal = ({
  updateCheckoutLimitsFormProps,
  updateCheckoutLimitsHandler,
  modalErrorMsg,
  isModalLoading,
  setShowModal,
}: Props): ReactElement => {
  const [hideError, setHideError] = useState<boolean>(false);

  const validationSchema = Yup.object().shape({
    minLimit: Yup.string()
      .when('useSysDefaultMinLimit', {
        is: false,
        then: Yup.string()
          .required('Amount is required')
          .matches(/^\d+(\.\d{1,2})?$/, 'Invalid amount. Use up to 2 decimal places.'),
      })
      .nullable(),
    maxLimit: Yup.string()
      .when('useSysDefaultMaxLimit', {
        is: false,
        then: Yup.string()
          .required('Amount is required')
          .matches(/^\d+(\.\d{1,2})?$/, 'Invalid amount. Use up to 2 decimal places.'),
      })
      .nullable(),
  });

  const defaultValues: UpdateCheckoutLimitsFormProps = {
    minLimit: '',
    useSysDefaultMinLimit: false,
    maxLimit: '',
    useSysDefaultMaxLimit: false,
  };

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<UpdateCheckoutLimitsFormProps>({
    resolver: yupResolver(validationSchema),
    defaultValues: updateCheckoutLimitsFormProps ?? defaultValues,
  });

  const onSubmit = useCallback(
    async (formData) => {
      updateCheckoutLimitsHandler(formData);
    },
    [updateCheckoutLimitsHandler],
  );

  const handleFormSubmit = useCallback(() => {
    return handleSubmit(onSubmit);
  }, [handleSubmit, onSubmit]);

  const handleCancel = () => {
    setShowModal(false);
  };

  useEffect(() => {
    if (modalErrorMsg) {
      setHideError(false);
    }
  }, [modalErrorMsg]);

  const useSysDefaultMinLimit = watch('useSysDefaultMinLimit');
  const useSysDefaultMaxLimit = watch('useSysDefaultMaxLimit');

  return (
    <Modal
      title={'Update checkout limits'}
      cancelBtnText="Cancel"
      confirmBtnText={'Update'}
      handleCancel={handleCancel}
      handleSubmit={handleFormSubmit()}
      disableConfirmBtn={isModalLoading}
      isLoading={isModalLoading}
      large={false}
      testId={'update-checkout-limits-modal'}
    >
      {modalErrorMsg.length > 0 && !hideError && (
        <Message
          className="mt-2 mb-0"
          success={false}
          description={modalErrorMsg}
          handleClose={(e) => {
            e.preventDefault();
            setHideError(true);
          }}
        />
      )}
      <FormWrapper>
        <FormItem>
          <Label>Use system default minimum limit</Label>
          <FormCheckbox type="checkbox" {...register('useSysDefaultMinLimit')} />
          {errors.useSysDefaultMinLimit && <span className="error-msg">{errors.useSysDefaultMinLimit.message}</span>}
        </FormItem>
        {!useSysDefaultMinLimit && (
          <FormItem>
            <Label>Minimum limit</Label>
            <VInput {...register('minLimit')} isValid={!errors.minLimit} />
            {errors.minLimit && <span className="error-msg">{errors.minLimit.message}</span>}
          </FormItem>
        )}
        <FormItem>
          <Label>Use system default maximum limit</Label>
          <FormCheckbox type="checkbox" {...register('useSysDefaultMaxLimit')} />
          {errors.useSysDefaultMaxLimit && <span className="error-msg">{errors.useSysDefaultMaxLimit.message}</span>}
        </FormItem>
        {!useSysDefaultMaxLimit && (
          <FormItem>
            <Label>Maximum limit</Label>
            <VInput {...register('maxLimit')} isValid={!errors.maxLimit} />
            {errors.maxLimit && <span className="error-msg">{errors.maxLimit.message}</span>}
          </FormItem>
        )}
      </FormWrapper>
    </Modal>
  );
};

export default UpdateCheckoutLimitsModal;
